import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONSEQUENTIAL = ["loss_debrief", "payment_chase", "stage_change", "client_decision"];

async function postComplete(taskId, outcome, payload) {
  const res = await fetch(`${API}/scrum/v2/tasks/${taskId}/complete`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ outcome, payload }),
  });
  let data = {};
  try { data = await res.json(); } catch (e) { /* noop */ }
  return { ok: res.ok, status: res.status, data: data?.detail || data };
}

// The ONE completion step. Every surface (Board, /scrum list, Today, edit dialog)
// opens this; it is the only way to finish/cancel a task from the UI.
export function TaskCompletionDialog({ task, open, onOpenChange, onDone, initialOutcome = null }) {
  const [outcome, setOutcome] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // waiting fields
  const [dep, setDep] = useState(""); const [cp, setCp] = useState("");
  const [reason, setReason] = useState(""); const [startDate, setStartDate] = useState("");
  const [chaseDate, setChaseDate] = useState("");
  // loss debrief
  const [wasLost, setWasLost] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [oppId, setOppId] = useState("");
  const [vocab, setVocab] = useState([]);
  const [lossReason, setLossReason] = useState("");
  const [competitor, setCompetitor] = useState("");
  const [confirmLost, setConfirmLost] = useState(false);

  const ttype = (task?.task_type || "").toLowerCase();
  const isLoss = ttype === "loss_debrief";
  const isConsequential = CONSEQUENTIAL.includes(ttype);

  useEffect(() => {
    if (open) {
      setOutcome(initialOutcome); setNote(""); setDep(""); setCp(""); setReason("");
      setStartDate(initialOutcome === "waiting" ? new Date().toISOString().slice(0, 10) : "");
      setChaseDate(""); setWasLost(""); setCandidates([]);
      setOppId(""); setVocab([]); setLossReason(""); setCompetitor(""); setConfirmLost(false);
    }
  }, [open, task?.task_id, initialOutcome]);

  if (!task) return null;

  const finish = async (o, payload) => {
    setBusy(true);
    const r = await postComplete(task.task_id, o, { ...payload, retrospective: !!task._retro });
    setBusy(false);
    if (r.status === 409 && (r.data?.candidates || r.data?.vocabulary)) {
      if (r.data.candidates) { setCandidates(r.data.candidates); toast.info("More than one deal could apply — pick one."); }
      if (r.data.vocabulary) { setVocab(r.data.vocabulary); toast.info("Choose a loss reason from Triage."); }
      return;
    }
    if (!r.ok) { toast.error(r.data?.error || "Could not complete task"); return; }
    toast.success(`Outcome recorded: ${r.data?.resolution_type || r.data?.status}`);
    onOpenChange(false);
    onDone && onDone(r.data);
  };

  const submitLoss = async () => {
    const payload = { note, was_lost: wasLost, opportunity_id: oppId || undefined };
    if (wasLost === "yes") {
      payload.loss_reason = lossReason; payload.confirm = confirmLost;
      if (competitor) payload.competitor = competitor;
    }
    await finish("finished", payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="task-completion-dialog">
        <DialogHeader>
          <DialogTitle>{isLoss ? "Loss debrief" : "Complete task — what happened?"}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600 mb-2">"{task.title}"</p>

        {isLoss ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 text-amber-700 bg-amber-50 p-2 rounded text-xs">
              <AlertTriangle className="w-4 h-4 mt-0.5" /> A deal is only closed as lost when you explicitly confirm it below.
            </div>
            <div>
              <Label>Was the deal lost?</Label>
              <div className="flex gap-2 mt-1">
                {["yes", "no", "unsure"].map(v => (
                  <Button key={v} type="button" variant={wasLost === v ? "default" : "outline"} size="sm"
                    data-testid={`loss-was-${v}`} onClick={() => setWasLost(v)}>{v}</Button>
                ))}
              </div>
            </div>
            {(candidates.length > 0) && (
              <div>
                <Label>Which deal?</Label>
                <select className="w-full border rounded p-2 text-sm mt-1" value={oppId}
                  data-testid="loss-deal-select" onChange={e => setOppId(e.target.value)}>
                  <option value="">Select a deal…</option>
                  {candidates.map(c => <option key={c.opportunity_id} value={c.opportunity_id}>{c.opportunity_name} ({c.stage})</option>)}
                </select>
              </div>
            )}
            {wasLost === "yes" && (
              <>
                <div>
                  <Label>Loss reason (Triage)</Label>
                  <select className="w-full border rounded p-2 text-sm mt-1" value={lossReason}
                    data-testid="loss-reason-select" onChange={e => setLossReason(e.target.value)}>
                    <option value="">Select…</option>
                    {(vocab.length ? vocab : ["lost_to_competitor","no_decision","budget","timing_deferred","done_in_house"]).map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
                {lossReason === "lost_to_competitor" && (
                  <div><Label>Competitor</Label><Input value={competitor} data-testid="loss-competitor" onChange={e => setCompetitor(e.target.value)} /></div>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={confirmLost} data-testid="loss-confirm" onCheckedChange={v => setConfirmLost(!!v)} />
                  I confirm this deal should be closed as lost.
                </label>
              </>
            )}
            <div>
              <Label>What happened? (required)</Label>
              <Textarea value={note} data-testid="loss-note" onChange={e => setNote(e.target.value)} rows={3} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button data-testid="loss-submit" disabled={busy || !wasLost || !note.trim() || (wasLost === "yes" && (!lossReason || !confirmLost))}
                onClick={submitLoss}>Record debrief</Button>
            </DialogFooter>
          </div>
        ) : !outcome ? (
          <div className="grid gap-2">
            <Button variant="outline" className="justify-start" data-testid="outcome-finished" onClick={() => setOutcome("finished")}>
              <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" /> Finished
            </Button>
            <Button variant="outline" className="justify-start" data-testid="outcome-waiting" onClick={() => setOutcome("waiting")}>
              <Clock className="w-4 h-4 mr-2 text-amber-600" /> Now waiting on someone / something
            </Button>
            <Button variant="outline" className="justify-start" data-testid="outcome-not-needed" onClick={() => setOutcome("not_needed")}>
              <XCircle className="w-4 h-4 mr-2 text-slate-500" /> No longer needed
            </Button>
          </div>
        ) : outcome === "finished" ? (
          <div className="space-y-3">
            <div>
              <Label>Note {isConsequential ? "(required)" : "(optional)"}</Label>
              <Textarea value={note} data-testid="finished-note" onChange={e => setNote(e.target.value)} rows={3} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOutcome(null)}>Back</Button>
              <Button data-testid="finished-submit" disabled={busy || (isConsequential && !note.trim())}
                onClick={() => finish("finished", { note })}>Mark finished</Button>
            </DialogFooter>
          </div>
        ) : outcome === "waiting" ? (
          <div className="space-y-3">
            <div><Label>Waiting on (dependency type) *</Label>
              <Input value={dep} data-testid="wait-dep" onChange={e => setDep(e.target.value)} placeholder="client / colleague / vendor / event" /></div>
            <div><Label>Counterparty</Label><Input value={cp} data-testid="wait-cp" onChange={e => setCp(e.target.value)} /></div>
            <div><Label>Reason *</Label><Textarea value={reason} data-testid="wait-reason" onChange={e => setReason(e.target.value)} rows={2} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Start date *</Label><Input type="date" data-testid="wait-start" value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
              <div><Label>Chase date *</Label><Input type="date" data-testid="wait-chase" value={chaseDate} onChange={e => setChaseDate(e.target.value)} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOutcome(null)}>Back</Button>
              <Button data-testid="wait-submit" disabled={busy || !dep.trim() || !reason.trim() || !startDate || !chaseDate}
                onClick={() => finish("waiting", { dependency_type: dep, counterparty: cp, reason, start_date: startDate, chase_date: chaseDate })}>
                Convert to wait</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label>Why is this no longer needed? *</Label>
              <Textarea value={reason} data-testid="notneeded-reason" onChange={e => setReason(e.target.value)} rows={3} /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOutcome(null)}>Back</Button>
              <Button data-testid="notneeded-submit" disabled={busy || !reason.trim()}
                onClick={() => finish("not_needed", { reason })}>Cancel task</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TaskCompletionDialog;
